/**
 * FontMetadataResolver - Utility for resolving complete font metadata for WASM operations
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for resolving all font metadata needed for WASM
 * operations, including UID, family, and postscript name.
 */

import { resolveFontUidSafe } from './FontResolverWrapper';

/**
 * Complete font metadata for WASM operations
 */
export interface FontMetadata {
  wasmFontUid: string;
  wasmFontFamily: string;
  wasmPostscriptName: string;
  fontFamily: string;
  fontWeight: number;
  fontStyle: 'normal' | 'italic' | 'oblique';
}

/**
 * Resolve complete font metadata for WASM operations
 * 
 * Resolves the font UID and constructs all metadata needed for WASM pagination
 * and layout operations. This includes:
 * - wasmFontUid: Resolved font UID (or fallback to fontFamily)
 * - wasmFontFamily: Original font family name
 * - wasmPostscriptName: Same as wasmFontUid (used as postscript name)
 * 
 * @param fontFamily - Font family name
 * @param fontWeight - Font weight (default: 400)
 * @param fontStyle - Font style (default: 'normal')
 * @returns Complete font metadata object
 */
export function resolveFontMetadata(
  fontFamily: string,
  fontWeight: number = 400,
  fontStyle: 'normal' | 'italic' | 'oblique' = 'normal'
): FontMetadata {
  const wasmFontUid = resolveFontUidSafe(fontFamily, fontWeight, fontStyle);
  const wasmFontFamily = fontFamily;
  const wasmPostscriptName = wasmFontUid;
  
  return {
    wasmFontUid,
    wasmFontFamily,
    wasmPostscriptName,
    fontFamily,
    fontWeight,
    fontStyle
  };
}

/**
 * Log resolved font metadata (for debugging)
 * 
 * @param metadata - Font metadata to log
 * @param fontSizePx - Font size in pixels (optional, for logging)
 */
export function logFontMetadata(
  metadata: FontMetadata,
  fontSizePx?: number
): void {
  const sizeInfo = fontSizePx !== undefined ? ` size=${fontSizePx}` : '';
  console.log(
    `🔤 [TypesetterWorkerClient] Resolved font: family="${metadata.wasmFontFamily}" uid="${metadata.wasmFontUid}"${sizeInfo} weight=${metadata.fontWeight} style=${metadata.fontStyle}`
  );
}

